import axios from "axios";

const http = axios.create();

http.defaults.baseURL = `${process.env.REACT_APP_API_URL}/api`;
http.defaults.headers.common.Accept = "application/json";

// Cola de peticiones que esperan un nuevo token mientras se refresca
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Interceptor para añadir JWT token en cada request
http.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("access");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejar respuestas y renovar token en 401
http.interceptors.response.use(
  (response) => {
    // Guardar tokens si vienen en la respuesta (después de login/refresh)
    if (response.data?.access) {
      localStorage.setItem("access", response.data.access);
    }
    if (response.data?.refresh) {
      localStorage.setItem("refresh", response.data.refresh);
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Si es 401 y no es ya un reintento ni una llamada a refresh/login
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes("/auth/jwt/")
    ) {
      if (isRefreshing) {
        // Encolar la petición hasta que el refresh termine
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return http(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem("refresh");
      if (!refreshToken) {
        isRefreshing = false;
        processQueue(error, null);
        return Promise.reject(error);
      }

      try {
        const refreshResponse = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/auth/jwt/refresh/`,
          { refresh: refreshToken }
        );
        const newAccessToken = refreshResponse.data.access;
        localStorage.setItem("access", newAccessToken);
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        processQueue(null, newAccessToken);
        isRefreshing = false;
        return http(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.removeItem("access");
        localStorage.removeItem("refresh");
        isRefreshing = false;
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default http;
